import { FC } from 'react';
import { Formik, Form } from 'formik';
import { Box } from '@mui/material';

import { SearchIcon } from '../../../../../components/Icons';
import { FlightSearchFormData, FLIGHT_SEARCH_INITIAL_VALUES } from '../../../../../types/flight.types';

import styles from './FlightSearchForm.module.scss';
import AirportSelect from '../../../../../common/AirportSelect';
import CustomReactDatePicker from '../../../../../common/CustomReactDatePicker';
import CustomButton from '../../../../../common/CustomButton';
import TravelersSelect from './TravelersSelect';

interface FlightSearchFormProps {
  onSubmit: (values: FlightSearchFormData) => void | Promise<void>;
  isLoading?: boolean;
  isMobileLayout?: boolean;
}

const FlightSearchForm: FC<FlightSearchFormProps> = ({ onSubmit, isLoading = false, isMobileLayout = false }) => {
  const formikValidate = (values: FlightSearchFormData) => {
    const errors: Partial<FlightSearchFormData> = {};

    if (!values.flightFrom?.trim()) {
      errors.flightFrom = 'Departure airport is required';
    }

    if (!values.flightTo?.trim()) {
      errors.flightTo = 'Arrival airport is required';
    }

    if (!values.departDate) {
      errors.departDate = 'Departure date is required';
    }

    if (values.returnDate && values.departDate && values.returnDate < values.departDate) {
      errors.returnDate = 'Return date must be after departure date';
    }

    if (!values.travelersCount || values.travelersCount < 1) {
      // @ts-expect-error
      errors.travelersCount = 'At least 1 traveler required';
    }

    if (!values.travelClass) {
      // @ts-expect-error
      errors.travelClass = 'Travel class is required';
    }

    return errors;
  };

  return (
    <Box className={styles.flightSearchFormContainer}>
      <Formik
        initialValues={FLIGHT_SEARCH_INITIAL_VALUES}
        validate={formikValidate}
        onSubmit={async (values, { resetForm }) => {
          await onSubmit(values);
          resetForm({
            values: { ...values },
            submitCount: 0,
            touched: {},
            errors: {},
          });
        }}
        enableReinitialize
      >
        {({ values, handleChange, errors, submitCount, setFieldValue, setValues }) => {
          const showSubmitErrors = submitCount > 0;
          const reserveDesktopSlots = !isMobileLayout;

          const flightErrorFieldOrder = [
            'flightFrom',
            'flightTo',
            'departDate',
            'returnDate',
            'travelersCount',
            'travelClass',
          ] as const;

          const firstErrorField = showSubmitErrors
            ? flightErrorFieldOrder.find(field => errors[field])
            : undefined;

          const fieldShowsError = (field: (typeof flightErrorFieldOrder)[number]) =>
            showSubmitErrors && firstErrorField === field;

          return (
            <Form className={styles.tripForm}>
              <Box className={styles.formGrid}>
                <Box className={styles.formField}>
                  <AirportSelect
                    name="flightFrom"
                    inputLabel="From"
                    searchType="airport"
                    value={values.flightFrom}
                    onSelectionChange={(code, details) => {
                      setValues(
                        {
                          ...values,
                          flightFrom: code,
                          flightFrom_details: details ?? undefined,
                        },
                        true
                      );
                    }}
                    details={values.flightFrom_details}
                    error={fieldShowsError('flightFrom') && errors.flightFrom ? String(errors.flightFrom) : ''}
                    showLocationIcon
                    reserveErrorSlot={reserveDesktopSlots}
                    compactForMobile={isMobileLayout}
                  />
                </Box>

                <Box className={styles.formField}>
                  <AirportSelect
                    name="flightTo"
                    inputLabel="To"
                    searchType="airport"
                    value={values.flightTo}
                    onSelectionChange={(code, details) => {
                      setValues(
                        {
                          ...values,
                          flightTo: code,
                          flightTo_details: details ?? undefined,
                        },
                        true
                      );
                    }}
                    details={values.flightTo_details}
                    error={fieldShowsError('flightTo') && errors.flightTo ? String(errors.flightTo) : ''}
                    showLocationIcon
                    reserveErrorSlot={reserveDesktopSlots}
                    compactForMobile={isMobileLayout}
                  />
                </Box>

                <Box className={styles.formField}>
                  <CustomReactDatePicker
                    name="departDate"
                    inputLabel="Depart"
                    value={values.departDate}
                    onChange={handleChange}
                    error={fieldShowsError('departDate') && !!errors.departDate}
                    helperText={fieldShowsError('departDate') && errors.departDate ? String(errors.departDate) : ''}
                    dateFormat="MMM d, yyyy"
                    calendarPreset="flightDiscover"
                    reserveHelperSpace={reserveDesktopSlots}
                    compactForMobile={isMobileLayout}
                    flightRange={{
                      rangeRole: 'start',
                      startIso: values.departDate,
                      endIso: values.returnDate,
                    }}
                    allowClear
                  />
                </Box>

                <Box className={styles.formField}>
                  <CustomReactDatePicker
                    name="returnDate"
                    inputLabel="Return (optional)"
                    value={values.returnDate}
                    onChange={handleChange}
                    error={fieldShowsError('returnDate') && !!errors.returnDate}
                    helperText={fieldShowsError('returnDate') && errors.returnDate ? String(errors.returnDate) : ''}
                    min={values.departDate}
                    dateFormat="MMM d, yyyy"
                    calendarPreset="flightDiscover"
                    reserveHelperSpace={reserveDesktopSlots}
                    compactForMobile={isMobileLayout}
                    flightRange={{
                      rangeRole: 'end',
                      startIso: values.departDate,
                      endIso: values.returnDate,
                    }}
                    allowClear
                  />
                </Box>

                <Box className={styles.formField}>
                  <TravelersSelect
                    travelersCount={values.travelersCount}
                    travelClass={values.travelClass}
                    onTravelersCountChange={n => setFieldValue('travelersCount', n, true)}
                    onTravelClassChange={c => setFieldValue('travelClass', c, true)}
                    invalid={
                      fieldShowsError('travelersCount') ||
                      fieldShowsError('travelClass')
                    }
                    compactForMobile={isMobileLayout}
                  />
                  {reserveDesktopSlots ? (
                    <Box
                      className={styles.fieldErrorReserve}
                      aria-hidden
                    />
                  ) : null}
                </Box>

                <Box
                  className={`${styles.submitButtonWrapper} ${
                    isMobileLayout ? styles.submitButtonWrapperWide : ''
                  }`}
                >
                  <CustomButton
                    btnType="submit"
                    variant="contained"
                    loading={isLoading}
                    startIcon={<SearchIcon color="#FFFFFF" size={24} />}
                  >
                    {isMobileLayout ? 'Find Flights' : ''}
                  </CustomButton>
                  {reserveDesktopSlots ? (
                    <Box
                      className={styles.fieldErrorReserve}
                      aria-hidden
                    />
                  ) : null}
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default FlightSearchForm;
